import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Organization } from '../../types';
import { useNotification } from '../../context/NotificationContext';
import { organizationService } from '../../services';

interface ServiceLimitsCardProps {
  organization: Organization;
  onUpdate: (updatedOrganization: Organization) => void;
}

const ServiceLimitsCard: React.FC<ServiceLimitsCardProps> = ({
  organization,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  const [analysisCredits, setAnalysisCredits] = useState(organization.analysisCredits?.toString() || '');
  const [loading, setLoading] = useState(false);

  const handleEdit = () => {
    setAnalysisCredits(organization.analysisCredits?.toString() || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setAnalysisCredits(organization.analysisCredits?.toString() || '');
    setIsEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Validierung
      const creditsNumber = analysisCredits === '' ? undefined : parseInt(analysisCredits, 10);

      if (analysisCredits !== '' && (isNaN(creditsNumber!) || creditsNumber! <= 0)) {
        showNotification(t('admin:messages.invalidCreditsValue'), 'error');
        return;
      }

      // Backend-API-Call
      const response = await organizationService.updateAnalysisCredits(
        organization._id,
        creditsNumber
      );

      // Organisation mit neuen Credits aktualisieren
      const updatedOrganization = {
        ...organization,
        analysisCredits: response.data.analysisCredits,
        usedCredits: response.data.usedCredits,
        remainingCredits: response.data.remainingCredits,
        creditsUnlimited: response.data.creditsUnlimited
      };

      onUpdate(updatedOrganization);
      setIsEditing(false);
      showNotification(
        creditsNumber
          ? `Analyse-Credits auf ${creditsNumber} gesetzt`
          : 'Analyse-Credits entfernt (unbegrenzt)',
        'success'
      );
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Analyse-Credits:', error);
      showNotification(t('admin:messages.creditsUpdateError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const usedCredits = organization.usedCredits ?? 0;
  const pendingCredits = (organization.pendingAnalyses ?? 0) + (organization.analyzingAnalyses ?? 0);
  const maxCredits = organization.analysisCredits;
  const remainingCredits = organization.remainingCredits ?? (maxCredits ? Math.max(0, maxCredits - usedCredits - pendingCredits) : null);
  const isLimitReached = maxCredits ? (usedCredits + pendingCredits) >= maxCredits : false;
  const usagePercentage = maxCredits ? Math.min(((usedCredits + pendingCredits) / maxCredits) * 100, 100) : 0;

  return (
    <Card sx={{
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      borderRadius: '8px',
      bgcolor: 'background.paper',
      border: isLimitReached ? '2px solid #ef4444' : '1px solid rgb(0 0 0 / 0.05)',
      minHeight: '150px'
    }}>
      <CardContent sx={{ p: 2 }}>
        {isEditing ? (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5
            }}>
              <Typography variant="subtitle1" sx={{
                color: isDark ? '#93c5fd' : '#1f2937',
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                Credits
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Max. Analyse-Credits"
              type="number"
              value={analysisCredits}
              onChange={(e) => setAnalysisCredits(e.target.value)}
              placeholder="Unbegrenzt"
              inputProps={{ min: 1 }}
              size="small"
              sx={{ mb: 1.5 }}
              data-testid="analysis-credits-input"
            />

            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={handleCancel}
                data-testid="analysis-credits-cancel-button"
              >
                <Close sx={{ fontSize: '1.4rem' }} />
                <Typography sx={{
                  color: 'primary.dark',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem'
                }}>
                  Abbrechen
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                data-testid="analysis-credits-save-button"
              >
                Speichern
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5
            }}>
              <Typography variant="subtitle1" sx={{
                color: isDark ? '#93c5fd' : '#1f2937',
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                Credits
              </Typography>

              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{ color: 'text.secondary', p: 0.5 }}
                data-testid="analysis-credits-edit-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Kompakte Anzeige */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h5" sx={{
                  color: isLimitReached ? '#ef4444' : '#16a34a',
                  fontWeight: 600,
                  fontSize: '1.25rem'
                }}>
                  {usedCredits + pendingCredits}
                </Typography>
                {maxCredits && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    / {maxCredits}
                  </Typography>
                )}
                {!maxCredits && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    / ∞
                  </Typography>
                )}
              </Box>

              {maxCredits && (
                <Typography variant="caption" sx={{
                  color: '#d97706',
                  fontWeight: 500,
                  fontSize: '0.75rem'
                }}>
                  ({pendingCredits} laufend)
                </Typography>
              )}
            </Box>

            {/* Kompakte Status-Anzeige */}
            {isLimitReached && (
              <Box sx={{
                p: 1,
                bgcolor: '#fef2f2',
                borderRadius: '4px',
                border: '1px solid #fecaca'
              }}>
                <Typography variant="caption" sx={{
                  color: '#dc2626',
                  fontWeight: 500,
                  fontSize: '0.75rem'
                }}>
                  ⚠️ Credit-Limit erreicht
                </Typography>
              </Box>
            )}

          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceLimitsCard; 